import { useState, useEffect, useCallback, useRef } from 'react';
import { startKyc, waitForKyc } from '../lib/kyc';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { toast } from 'sonner';
import { 
  Shield, 
  AlertTriangle, 
  Lock, 
  Unlock,
  Activity,
  Clock,
  ChevronRight,
  Camera,
  CheckCircle2,
  XCircle,
  Loader2,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../components/ui/input-otp';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Partner logo colors
const PARTNER_COLORS = {
  gcash: '#007DFE',
  maya: '#00D084',
  grabpay: '#00B14F',
  bdo: '#0066B3',
  bpi: '#A81C32',
  unionbank: '#FF6B00',
};

// Lock status badge component
const LockStatusBadge = ({ status }) => {
  const config = {
    SAFE: { bg: 'bg-[#10B981]/20', border: 'border-[#10B981]/30', text: 'text-[#10B981]', label: 'SAFE' },
    PARTIAL_LOCK: { bg: 'bg-[#FFCC00]/20', border: 'border-[#FFCC00]/30', text: 'text-[#FFCC00]', label: 'PARTIAL LOCK' },
    FULL_LOCK: { bg: 'bg-[#FF3B30]/20', border: 'border-[#FF3B30]/30', text: 'text-[#FF3B30]', label: 'FULL LOCK' },
  };
  const c = config[status] || config.SAFE;
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${c.bg} ${c.border} ${c.text} border`}>
      {c.label}
    </span>
  );
};

export default function DashboardPage() {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockStatus, setLockStatus] = useState('SAFE');
  const [hasPanicPin, setHasPanicPin] = useState(false);
  
  // Modal states
  const [showPanicModal, setShowPanicModal] = useState(false);
  const [showPinSetupModal, setShowPinSetupModal] = useState(false);
  const [showPinEntryModal, setShowPinEntryModal] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);
  
  // Scoped panic state
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [cards, setCards] = useState([]);
  const [panicPin, setPanicPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLocked, setPinLocked] = useState(false);
  const [lockRemaining, setLockRemaining] = useState(0);
  
  // KYC state
  const [kycStatus, setKycStatus] = useState('idle');
  const [kycFrameUrl, setKycFrameUrl] = useState(null);
  const kycRunRef = useRef(0);
  const [activating, setActivating] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, auditRes, pinStatusRes] = await Promise.all([
        api.get('/panic/status'),
        api.get('/audit?limit=10'),
        api.get('/panic/pin/status'),
      ]);
      setPartners(statusRes.data.partners);
      setCards(statusRes.data.cards || []);
      setLockStatus(statusRes.data.lock_status);
      setAuditLogs(auditRes.data.logs);
      setHasPanicPin(pinStatusRes.data.has_pin);
      setPinLocked(pinStatusRes.data.pin_locked);
      setLockRemaining(pinStatusRes.data.lock_remaining_seconds);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Lock remaining countdown
  useEffect(() => {
    if (lockRemaining > 0) {
      const timer = setInterval(() => {
        setLockRemaining(prev => {
          if (prev <= 1) {
            setPinLocked(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockRemaining]);

  const protectedPartners = partners.filter(p => p.protected);
  const lockedPartners = partners.filter(p => p.locked);
  const activeCards = cards.filter(c => c.status === 'active');
  const lockedCards = cards.filter(c => c.status === 'panic_locked');

  const handlePanicClick = () => {
    if (user?.panic_mode) {
      navigate('/recovery');
      return;
    }
    
    // Check if user has panic PIN
    if (!hasPanicPin) {
      setShowPinSetupModal(true);
      return;
    }
    
    // Initialize selected partners (all protected by default) and cards
    setSelectedPartners(protectedPartners.map(p => p.id));
    setSelectedCards(activeCards.map(c => c.id));
    setShowPanicModal(true);
  };

  const handlePinSetup = async () => {
    setPinError('');
    
    if (!newPin || newPin.length < 6 || !newPin.match(/^\d+$/)) {
      setPinError('PIN must be at least 6 digits');
      return;
    }
    
    if (newPin !== confirmPin) {
      setPinError('PINs do not match');
      return;
    }
    
    try {
      await api.post('/panic/pin/setup', {
        pin: newPin,
        confirm_pin: confirmPin,
      });
      toast.success('Panic PIN created successfully');
      setShowPinSetupModal(false);
      setNewPin('');
      setConfirmPin('');
      setHasPanicPin(true);
      // Open panic modal after PIN setup
      setSelectedPartners(protectedPartners.map(p => p.id));
      setShowPanicModal(true);
    } catch (error) {
      setPinError(error.response?.data?.detail || 'Failed to create PIN');
    }
  };

  const handlePartnerSelect = (partnerId, checked) => {
    if (checked) {
      setSelectedPartners([...selectedPartners, partnerId]);
    } else {
      setSelectedPartners(selectedPartners.filter(id => id !== partnerId));
    }
  };

  const handleCardSelect = (cardId, checked) => {
    if (checked) {
      setSelectedCards([...selectedCards, cardId]);
    } else {
      setSelectedCards(selectedCards.filter(id => id !== cardId));
    }
  };

  const proceedToPinEntry = () => {
    if (selectedPartners.length === 0 && selectedCards.length === 0) {
      toast.error('Please select at least one account or card');
      return;
    }
    setShowPanicModal(false);
    setPanicPin('');
    setPinError('');
    setShowPinEntryModal(true);
  };

  const verifyPinAndProceed = async () => {
    if (panicPin.length < 6) {
      setPinError('Please enter your 6-digit PIN');
      return;
    }
    
    try {
      await api.post('/panic/pin/verify', { pin: panicPin });
      setShowPinEntryModal(false);
      startKycVerification();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid PIN';
      setPinError(detail);
      if (error.response?.status === 429) {
        setPinLocked(true);
        // Extract remaining time from message
        const match = detail.match(/(\d+) seconds/);
        if (match) {
          setLockRemaining(parseInt(match[1]));
        }
      }
      setPanicPin('');
    }
  };

  const startKycVerification = async () => {
    setShowKycModal(true);
    setKycStatus('scanning');
    setKycFrameUrl(null);
    const run = ++kycRunRef.current;

    try {
      const started = await startKyc(api);
      if (started.provider === 'ekyc') {
        setKycFrameUrl(started.url);
        const result = await waitForKyc(api, () => kycRunRef.current !== run);
        if (result === 'cancelled') return;
        setKycFrameUrl(null);
        if (result === 'verified') {
          setKycStatus('success');
        } else {
          setKycStatus('failed');
          toast.error(result?.message || 'Verification did not complete. Please try again.');
        }
        return;
      }
    } catch (err) {
      setKycFrameUrl(null);
      setKycStatus('failed');
      toast.error(err.response?.data?.detail || 'Could not start verification');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Simulate KYC verification
      setTimeout(async () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        try {
          // The server records the verification; the client cannot assert it
          await api.post('/kyc/liveness');
          setKycStatus('success');
        } catch (err) {
          setKycStatus('failed');
          toast.error(err.response?.data?.detail || 'Verification failed');
        }
      }, 3000);
    } catch (error) {
      console.error('Camera access denied:', error);
      setKycStatus('failed');
      toast.error('Camera access denied. Please allow camera access for KYC verification.');
    }
  };

  const activatePanicMode = async () => {
    setActivating(true);
    try {
      const response = await api.post('/panic/activate', {
        confirm: true,
        selected_partners: selectedPartners,
        selected_cards: selectedCards,
        panic_pin: panicPin,
        panic_level: 'EMERGENCY_FULL',
      });
      
      toast.success(response.data.message);
      setShowKycModal(false);
      setPanicPin('');
      await refreshUser();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate panic mode');
    } finally {
      setActivating(false);
    }
  };

  const closeKycModal = () => {
    kycRunRef.current += 1;
    setKycFrameUrl(null);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setShowKycModal(false);
    setKycStatus('idle');
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionLabel = (action) => {
    const labels = {
      'USER_REGISTERED': 'Account Created',
      'LOGIN_SUCCESS': 'Login',
      'LOGIN_FAILED': 'Failed Login',
      'MFA_SETUP_INITIATED': 'MFA Setup Started',
      'MFA_SETUP_COMPLETED': 'MFA Enabled',
      'MFA_DISABLED': 'MFA Disabled',
      'PANIC_ACTIVATED': 'Panic Activated',
      'PANIC_PIN_CREATED': 'Panic PIN Created',
      'PANIC_PIN_VERIFIED': 'PIN Verified',
      'PANIC_PIN_FAILED': 'PIN Failed',
      'PARTNER_PROTECTION_TOGGLED': 'Protection Changed',
      'PARTNER_UNLOCKED': 'Partner Unlocked',
      'RECOVERY_INITIATED': 'Recovery Started',
      'RECOVERY_COMPLETED': 'Recovery Completed',
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-12 h-12"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl lg:text-4xl font-black text-[#E6F1FF]">Dashboard</h1>
              <LockStatusBadge status={lockStatus} />
            </div>
            <p className="text-[#8892B0]">Monitor and manage your security status</p>
          </div>
          
          <Button
            onClick={handlePanicClick}
            disabled={pinLocked}
            className={`
              px-8 py-6 rounded-xl font-black text-lg tracking-wider uppercase
              transition-all duration-200
              ${user?.panic_mode 
                ? 'bg-[#10B981] hover:bg-[#059669] text-white shadow-lg shadow-[#10B981]/30' 
                : pinLocked
                  ? 'bg-[#4A5568] text-[#8892B0] cursor-not-allowed'
                  : 'bg-[#FF3B30] hover:bg-[#E6352B] text-white shadow-lg shadow-[#FF3B30]/30 hover:shadow-[#FF3B30]/50 hover:scale-[1.02]'
              }
            `}
            data-testid="panic-button"
          >
            {user?.panic_mode ? (
              <>
                <Unlock className="w-5 h-5 mr-2" />
                Start Recovery
              </>
            ) : pinLocked ? (
              <>
                <Lock className="w-5 h-5 mr-2" />
                Locked ({Math.floor(lockRemaining / 60)}:{(lockRemaining % 60).toString().padStart(2, '0')})
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 mr-2" />
                Activate PanicLock
              </>
            )}
          </Button>
        </div>

        {/* Status Banner */}
        <div className={`
          p-6 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-4
          ${lockStatus === 'FULL_LOCK' 
            ? 'bg-gradient-to-r from-[#FF3B30]/20 to-[#FF3B30]/5 border border-[#FF3B30]/30' 
            : lockStatus === 'PARTIAL_LOCK'
              ? 'bg-gradient-to-r from-[#FFCC00]/20 to-[#FFCC00]/5 border border-[#FFCC00]/30'
              : 'bg-gradient-to-r from-[#10B981]/20 to-[#10B981]/5 border border-[#10B981]/30'
          }
        `} data-testid="status-banner">
          <div className={`
            w-14 h-14 rounded-xl flex items-center justify-center
            ${lockStatus === 'FULL_LOCK' ? 'bg-[#FF3B30]/20' : lockStatus === 'PARTIAL_LOCK' ? 'bg-[#FFCC00]/20' : 'bg-[#10B981]/20'}
          `}>
            {lockStatus === 'SAFE' ? (
              <Shield className="w-7 h-7 text-[#10B981]" />
            ) : (
              <Lock className={`w-7 h-7 ${lockStatus === 'FULL_LOCK' ? 'text-[#FF3B30]' : 'text-[#FFCC00]'}`} />
            )}
          </div>
          <div className="flex-1">
            <h2 className={`text-xl font-bold ${
              lockStatus === 'FULL_LOCK' ? 'text-[#FF3B30]' : 
              lockStatus === 'PARTIAL_LOCK' ? 'text-[#FFCC00]' : 'text-[#10B981]'
            }`}>
              {lockStatus === 'FULL_LOCK' ? 'SYSTEM LOCKED' : 
               lockStatus === 'PARTIAL_LOCK' ? 'PARTIALLY LOCKED' : 'System Safe'}
            </h2>
            <p className="text-[#8892B0] text-sm mt-1">
              {lockStatus === 'SAFE'
                ? `${protectedPartners.length} accounts protected. All systems operational.`
                : `${lockedPartners.length} of ${protectedPartners.length} protected accounts locked. Recovery required.`
              }
            </p>
          </div>
          {user?.panic_mode && user?.panic_activated_at && (
            <div className="text-right">
              <p className="text-xs text-[#8892B0]">Activated at</p>
              <p className="text-sm text-[#E6F1FF] font-mono">{formatTime(user.panic_activated_at)}</p>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="stat-card" data-testid="stat-total-partners">
            <div className="flex items-center justify-between mb-4">
              <Shield className="w-8 h-8 text-[#2BBBAD]" />
              <span className="text-xs text-[#8892B0] uppercase tracking-wider">Total</span>
            </div>
            <p className="text-4xl font-black text-[#E6F1FF]">{partners.length}</p>
            <p className="text-sm text-[#8892B0] mt-1">Partner Apps</p>
          </div>

          <div className="stat-card" data-testid="stat-protected">
            <div className="flex items-center justify-between mb-4">
              <Lock className="w-8 h-8 text-[#64FFDA]" />
              <span className="text-xs text-[#8892B0] uppercase tracking-wider">Protected</span>
            </div>
            <p className="text-4xl font-black text-[#E6F1FF]">{protectedPartners.length}</p>
            <p className="text-sm text-[#8892B0] mt-1">Active Protection</p>
          </div>

          <div className="stat-card" data-testid="stat-locked">
            <div className="flex items-center justify-between mb-4">
              <AlertTriangle className={`w-8 h-8 ${lockedPartners.length > 0 ? 'text-[#FF3B30]' : 'text-[#8892B0]'}`} />
              <span className="text-xs text-[#8892B0] uppercase tracking-wider">Locked</span>
            </div>
            <p className={`text-4xl font-black ${lockedPartners.length > 0 ? 'text-[#FF3B30]' : 'text-[#E6F1FF]'}`}>
              {lockedPartners.length}
            </p>
            <p className="text-sm text-[#8892B0] mt-1">Currently Locked</p>
          </div>

          <div className="stat-card" data-testid="stat-security">
            <div className="flex items-center justify-between mb-4">
              <Activity className="w-8 h-8 text-[#3B82F6]" />
              <span className="text-xs text-[#8892B0] uppercase tracking-wider">Security</span>
            </div>
            <p className="text-4xl font-black text-[#E6F1FF]">
              {user?.mfa_enabled && hasPanicPin ? 'MAX' : user?.mfa_enabled || hasPanicPin ? '2FA' : 'Basic'}
            </p>
            <p className="text-sm text-[#8892B0] mt-1">Auth Level</p>
          </div>
        </div>

        {/* Partners Preview & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Partners Preview */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-[#E6F1FF]">Partner Apps</h3>
              <button 
                onClick={() => navigate('/partners')}
                className="flex items-center gap-1 text-sm text-[#2BBBAD] hover:text-[#64FFDA]"
                data-testid="view-all-partners"
              >
                View All <ChevronRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {partners.slice(0, 6).map((partner) => (
                <div 
                  key={partner.id}
                  className={`
                    p-4 rounded-xl text-center relative
                    ${partner.locked 
                      ? 'bg-[#FF3B30]/10 border border-[#FF3B30]/30' 
                      : partner.protected 
                        ? 'bg-[#2BBBAD]/10 border border-[#2BBBAD]/30'
                        : 'bg-[#1A2F4B]/50 border border-white/5'
                    }
                  `}
                  data-testid={`partner-preview-${partner.code}`}
                >
                  <div 
                    className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2"
                    style={{ backgroundColor: PARTNER_COLORS[partner.code] || '#2BBBAD' }}
                  >
                    <span className="text-white font-bold text-lg">
                      {partner.name.charAt(0)}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-[#E6F1FF] truncate">{partner.name}</p>
                  <p className={`text-xs mt-1 ${partner.locked ? 'text-[#FF3B30]' : partner.protected ? 'text-[#2BBBAD]' : 'text-[#8892B0]'}`}>
                    {partner.locked ? 'Locked' : partner.protected ? 'Protected' : 'Not Protected'}
                  </p>
                  {partner.locked && partner.locked_at && (
                    <p className="text-[10px] text-[#8892B0] mt-1">
                      {new Date(partner.locked_at).toLocaleTimeString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-[#E6F1FF]">Recent Activity</h3>
              <button 
                onClick={() => navigate('/audit')}
                className="flex items-center gap-1 text-sm text-[#2BBBAD] hover:text-[#64FFDA]"
                data-testid="view-all-activity"
              >
                View All <ChevronRight size={16} />
              </button>
            </div>
            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
              {auditLogs.length === 0 ? (
                <p className="text-[#8892B0] text-center py-8">No recent activity</p>
              ) : (
                auditLogs.map((log, index) => (
                  <div 
                    key={log.id} 
                    className="flex items-start gap-3 animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className={`
                      w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                      ${log.action.includes('PANIC') ? 'bg-[#FF3B30]/20' : 
                        log.action.includes('RECOVERY') ? 'bg-[#10B981]/20' : 
                        log.action.includes('PIN') ? 'bg-[#FFCC00]/20' :
                        'bg-[#2BBBAD]/20'}
                    `}>
                      <Clock size={14} className={
                        log.action.includes('PANIC') ? 'text-[#FF3B30]' : 
                        log.action.includes('RECOVERY') ? 'text-[#10B981]' : 
                        log.action.includes('PIN') ? 'text-[#FFCC00]' :
                        'text-[#2BBBAD]'
                      } />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#E6F1FF]">{getActionLabel(log.action)}</p>
                      <p className="text-xs text-[#8892B0] mt-0.5">{formatTime(log.timestamp)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PIN Setup Modal */}
      <Dialog open={showPinSetupModal} onOpenChange={setShowPinSetupModal}>
        <DialogContent className="bg-[#112240] border-[#2BBBAD]/30 max-w-md">
          <DialogHeader>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2BBBAD]/20 flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#2BBBAD]" />
            </div>
            <DialogTitle className="text-2xl font-black text-[#E6F1FF] text-center">
              Create Your Panic PIN
            </DialogTitle>
            <DialogDescription className="text-center text-[#8892B0]">
              Set up a 6-digit PIN to secure your panic activation. This PIN will be required every time you trigger PanicLock.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#E6F1FF]">New PIN (6+ digits)</Label>
              <Input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter 6-digit PIN"
                maxLength={8}
                className="text-center text-2xl tracking-widest h-14 bg-[#0B1C2D] border-[#2BBBAD]/20 text-[#E6F1FF]"
                data-testid="pin-setup-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#E6F1FF]">Confirm PIN</Label>
              <Input
                type="password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                placeholder="Confirm PIN"
                maxLength={8}
                className="text-center text-2xl tracking-widest h-14 bg-[#0B1C2D] border-[#2BBBAD]/20 text-[#E6F1FF]"
                data-testid="pin-confirm-input"
              />
            </div>
            {pinError && (
              <p className="text-sm text-[#FF3B30] text-center">{pinError}</p>
            )}
          </div>

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setShowPinSetupModal(false);
                setNewPin('');
                setConfirmPin('');
                setPinError('');
              }}
              className="flex-1 border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePinSetup}
              className="flex-1 bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold"
              data-testid="pin-setup-submit"
            >
              Create PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scoped Panic Modal */}
      <Dialog open={showPanicModal} onOpenChange={setShowPanicModal}>
        <DialogContent className="bg-[#112240] border-[#FF3B30]/30 max-w-lg">
          <DialogHeader>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF3B30]/20 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-[#FF3B30]" />
            </div>
            <DialogTitle className="text-2xl font-black text-[#E6F1FF] text-center">
              Select Items to Lock
            </DialogTitle>
            <DialogDescription className="text-center text-[#8892B0]">
              Choose accounts and cards to lock. All are selected by default.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-[400px] overflow-y-auto space-y-6">
            {/* Accounts Section */}
            {protectedPartners.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-[#8892B0] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Shield size={14} />
                  Accounts ({protectedPartners.length})
                </h4>
                <div className="space-y-2">
                  {protectedPartners.map((partner) => (
                    <label
                      key={partner.id}
                      className={`
                        flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors
                        ${selectedPartners.includes(partner.id) 
                          ? 'bg-[#FF3B30]/10 border border-[#FF3B30]/30' 
                          : 'bg-[#0B1C2D]/50 border border-white/5 hover:border-[#64FFDA]/20'
                        }
                      `}
                      data-testid={`select-partner-${partner.code}`}
                    >
                      <Checkbox
                        checked={selectedPartners.includes(partner.id)}
                        onCheckedChange={(checked) => handlePartnerSelect(partner.id, checked)}
                        className="w-5 h-5 border-2 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
                      />
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: PARTNER_COLORS[partner.code] || '#2BBBAD' }}
                      >
                        <span className="text-white font-bold">{partner.name.charAt(0)}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#E6F1FF]">{partner.name}</p>
                        <p className="text-xs text-[#8892B0]">Online Banking</p>
                      </div>
                      {selectedPartners.includes(partner.id) && (
                        <Lock className="w-4 h-4 text-[#FF3B30]" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Cards Section */}
            {activeCards.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-[#8892B0] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                  </svg>
                  Cards ({activeCards.length})
                </h4>
                <div className="space-y-2">
                  {activeCards.map((card) => (
                    <label
                      key={card.id}
                      className={`
                        flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors
                        ${selectedCards.includes(card.id) 
                          ? 'bg-[#FF3B30]/10 border border-[#FF3B30]/30' 
                          : 'bg-[#0B1C2D]/50 border border-white/5 hover:border-[#64FFDA]/20'
                        }
                      `}
                      data-testid={`select-card-${card.last4}`}
                    >
                      <Checkbox
                        checked={selectedCards.includes(card.id)}
                        onCheckedChange={(checked) => handleCardSelect(card.id, checked)}
                        className="w-5 h-5 border-2 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
                      />
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: PARTNER_COLORS[card.bank_partner_id] || '#2BBBAD' }}
                      >
                        <span className="text-white font-bold text-sm">{card.bank_name?.charAt(0) || 'B'}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#E6F1FF]">
                          {card.bank_name} {card.card_type.charAt(0).toUpperCase() + card.card_type.slice(1)} Card
                        </p>
                        <p className="text-xs text-[#8892B0] font-mono">•••• {card.last4}</p>
                      </div>
                      {selectedCards.includes(card.id) && (
                        <Lock className="w-4 h-4 text-[#FF3B30]" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {protectedPartners.length === 0 && activeCards.length === 0 && (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 mx-auto text-[#8892B0] mb-3" />
                <p className="text-[#8892B0]">No items to protect. Enable protection on partners or add cards first.</p>
              </div>
            )}
          </div>

          <div className="p-3 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]/20">
            <p className="text-xs text-[#FF3B30]">
              <strong>Warning:</strong> Selected items will be locked instantly. Recovery requires verification.
            </p>
          </div>

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setShowPanicModal(false)}
              className="flex-1 border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
              data-testid="panic-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={proceedToPinEntry}
              disabled={selectedPartners.length === 0 && selectedCards.length === 0}
              className="flex-1 bg-[#FF3B30] hover:bg-[#E6352B] text-white font-bold disabled:opacity-50"
              data-testid="panic-proceed-button"
            >
              <Lock className="w-4 h-4 mr-2" />
              Secure Selected ({selectedPartners.length + selectedCards.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN Entry Modal */}
      <Dialog open={showPinEntryModal} onOpenChange={setShowPinEntryModal}>
        <DialogContent className="bg-[#112240] border-[#FFCC00]/30 max-w-md">
          <DialogHeader>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFCC00]/20 flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#FFCC00]" />
            </div>
            <DialogTitle className="text-2xl font-black text-[#E6F1FF] text-center">
              Enter Panic PIN
            </DialogTitle>
            <DialogDescription className="text-center text-[#8892B0]">
              Enter your 6-digit PIN to confirm panic activation
            </DialogDescription>
          </DialogHeader>

          <div className="py-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={panicPin}
                onChange={setPanicPin}
                data-testid="panic-pin-input"
              >
                <InputOTPGroup className="gap-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      className="w-12 h-14 text-xl font-bold bg-[#0B1C2D] border-[#FFCC00]/30 text-[#E6F1FF] rounded-lg"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            {pinError && (
              <p className="text-sm text-[#FF3B30] text-center mt-4">{pinError}</p>
            )}
          </div>

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setShowPinEntryModal(false);
                setPanicPin('');
                setPinError('');
              }}
              className="flex-1 border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
            >
              Cancel
            </Button>
            <Button
              onClick={verifyPinAndProceed}
              disabled={panicPin.length < 6}
              className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#0B1C2D] font-bold"
              data-testid="panic-pin-submit"
            >
              Verify & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KYC Verification Modal */}
      <Dialog open={showKycModal} onOpenChange={closeKycModal}>
        <DialogContent className="bg-[#112240] border-[#2BBBAD]/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E6F1FF] text-center">
              KYC Liveness Verification
            </DialogTitle>
            <DialogDescription className="text-center text-[#8892B0]">
              {kycStatus === 'scanning' && (kycFrameUrl ? 'Follow the steps below to verify your identity' : 'Please look at the camera for verification')}
              {kycStatus === 'success' && 'Verification successful!'}
              {kycStatus === 'failed' && 'Verification failed. Please try again.'}
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <div className={kycFrameUrl ? 'mx-auto w-full' : 'kyc-camera mx-auto'}>
              {kycFrameUrl && kycStatus === 'scanning' && (
                <iframe
                  title="Identity verification"
                  src={kycFrameUrl}
                  allow="camera; microphone"
                  className="w-full border-0 rounded-lg bg-white"
                  style={{ height: 520 }}
                  data-testid="kyc-frame"
                />
              )}
{kycStatus === 'scanning' && !kycFrameUrl && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              )}
              {kycStatus === 'success' && (
                <div className="w-full h-full flex items-center justify-center bg-[#10B981]/10">
                  <CheckCircle2 className="w-20 h-20 text-[#10B981]" />
                </div>
              )}
              {kycStatus === 'failed' && (
                <div className="w-full h-full flex items-center justify-center bg-[#FF3B30]/10">
                  <XCircle className="w-20 h-20 text-[#FF3B30]" />
                </div>
              )}
            </div>

            {kycStatus === 'scanning' && !kycFrameUrl && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 border-4 border-[#2BBBAD] rounded-full animate-pulse" />
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={closeKycModal}
              className="flex-1 border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
              data-testid="kyc-cancel-button"
            >
              Cancel
            </Button>
            {kycStatus === 'success' && (
              <Button
                onClick={activatePanicMode}
                disabled={activating}
                className="flex-1 bg-[#FF3B30] hover:bg-[#E6352B] text-white font-bold"
                data-testid="kyc-activate-button"
              >
                {activating ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Activate PanicLock
                  </>
                )}
              </Button>
            )}
            {kycStatus === 'failed' && (
              <Button
                onClick={startKycVerification}
                className="flex-1 bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold"
              >
                <Camera className="w-5 h-5 mr-2" />
                Retry
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
